// BottomSheet Context for synchronizing animations across components
// CREATED: Context to manage bottom sheet state and animation progress
// Features:
// - Track if any bottom sheet is currently open
// - Provide animation progress (0-1) for scale effects on background content
// - Register/unregister bottom sheets to avoid conflicts
// - Enable synchronized animations between sheet and background

import { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';
import { MotionValue, useMotionValue } from 'framer-motion';

interface BottomSheetContextValue {
  isAnySheetOpen: boolean;
  openProgress: MotionValue<number>;
  registerSheet: (id: string) => void;
  unregisterSheet: (id: string) => void;
  setProgress: (progress: number) => void;
}

const BottomSheetContext = createContext<BottomSheetContextValue | undefined>(undefined);

export function BottomSheetProvider({ children }: { children: ReactNode }) {
  const [activeSheets, setActiveSheets] = useState<Set<string>>(new Set());
  const openProgress = useMotionValue(0);

  const registerSheet = useCallback((id: string) => {
    setActiveSheets((prev) => new Set(prev).add(id));
  }, []);

  const unregisterSheet = useCallback((id: string) => {
    setActiveSheets((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const setProgress = useCallback((progress: number) => {
    openProgress.set(progress);
  }, [openProgress]);

  // Memoize the context value to prevent unnecessary re-renders
  const value: BottomSheetContextValue = useMemo(() => ({
    isAnySheetOpen: activeSheets.size > 0,
    openProgress,
    registerSheet,
    unregisterSheet,
    setProgress,
  }), [activeSheets.size, openProgress, registerSheet, unregisterSheet, setProgress]);

  return (
    <BottomSheetContext.Provider value={value}>
      {children}
    </BottomSheetContext.Provider>
  );
}

export function useBottomSheet() {
  const context = useContext(BottomSheetContext);
  if (context === undefined) {
    throw new Error('useBottomSheet must be used within a BottomSheetProvider');
  }
  return context;
}

// Optional hook for components that might not have the provider
export function useBottomSheetOptional() {
  return useContext(BottomSheetContext);
}
